import { Injectable } from '@angular/core';
import Address from '../../../smoothr-web-app-core/models/Address';
import { environment } from 'src/environments/environment';

export interface AddressSuggestion {
	placeId: string;
	description: string;
	mainText: string;
	secondaryText: string;
}

@Injectable({
	providedIn: 'root',
})
export class AutocompleteService {
	private autocompleteService: google.maps.places.AutocompleteService;
	private placesService: google.maps.places.PlacesService;

	constructor() {
		this.initializeServices();
	}

	private initializeServices() {
		if (typeof google !== 'undefined' && google.maps) {
			this.autocompleteService = new google.maps.places.AutocompleteService();
			// Create dummy element for PlacesService
			const dummyElement = document.createElement('div');
			this.placesService = new google.maps.places.PlacesService(dummyElement);
		}
	}

	getAddressSuggestions(input: string): Promise<AddressSuggestion[]> {
		return new Promise((resolve, reject) => {
			// Re-init if google lib loaded late
			if (!this.autocompleteService && typeof google !== 'undefined' && google.maps) {
				this.initializeServices();
			}

			if (!this.autocompleteService || !input) {
				resolve([]);
				return;
			}

			const request: google.maps.places.AutocompletionRequest = {
				input: input,
				types: ['address'], // Only get address suggestions
				componentRestrictions: {
					country: environment.countryList,
				},
			};

			this.autocompleteService.getPlacePredictions(request, (predictions, status) => {
				if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
					const suggestions: AddressSuggestion[] = predictions.map(pred => ({
						placeId: pred.place_id,
						description: pred.description,
						mainText: pred.structured_formatting?.main_text || '',
						secondaryText: pred.structured_formatting?.secondary_text || '',
					}));
					resolve(suggestions);
				} else {
					resolve([]);
				}
			});
		});
	}

	getPlaceDetails(placeId: string): Promise<google.maps.places.PlaceResult> {
		return new Promise((resolve, reject) => {
			if (!this.placesService && typeof google !== 'undefined' && google.maps) {
				this.initializeServices();
			}

			if (!this.placesService || !placeId) {
				reject(new Error('Places service not initialized or no placeId'));
				return;
			}

			const request: google.maps.places.PlaceDetailsRequest = {
				placeId: placeId,
				fields: ['address_components', 'formatted_address', 'geometry'],
			};

			this.placesService.getDetails(request, (place, status) => {
				if (status === google.maps.places.PlacesServiceStatus.OK && place) {
					resolve(place);
				} else {
					reject(new Error('Unable to get place details'));
				}
			});
		});
	}

	convertPlaceToAddress(place: google.maps.places.PlaceResult): Address {
		const address: Address = new Address();
		address.displayName = place.formatted_address;

		if (place.geometry) {
			address.lat = place.geometry.location?.lat();
			address.lng = place.geometry.location?.lng();
		}

		if (place.address_components) {
			place.address_components.forEach(component => {
				const types = component.types;

				if (types.includes('street_number')) {
					address.number = component.long_name;
				} else if (types.includes('route')) {
					address.street = component.long_name;
				} else if (types.includes('postal_code')) {
					address.postalCode = component.long_name;
				} else if (types.includes('locality')) {
					address.city = component.long_name;
				} else if (types.includes('country')) {
					address.country = component.long_name;
				}
			});
		}

		return address;
	}
}
